import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardEnhancedAPI } from '../services/api';
import { 
  TrendingUp, 
  ShoppingCart, 
  Package, 
  Truck, 
  DollarSign,
  Calendar,
  Users,
  AlertCircle,
  ArrowRight,
  Clock,
  Zap
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart,
  Bar,
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import toast from 'react-hot-toast';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const DashboardEnhanced = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [orderBreakdown, setOrderBreakdown] = useState(null);
  const [quickView, setQuickView] = useState(null);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchOrderBreakdown();
    }
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data in parallel
      const [statsRes, trendsRes, quickViewRes, subscriptionRes] = await Promise.all([
        dashboardEnhancedAPI.getStats(),
        dashboardEnhancedAPI.getRevenueTrends(),
        dashboardEnhancedAPI.getQuickView(),
        dashboardEnhancedAPI.getSubscriptionAnalytics()
      ]);

      setStats(statsRes.data.data);
      setRevenueTrends(trendsRes.data.data);
      setQuickView(quickViewRes.data.data);
      setSubscriptionAnalytics(subscriptionRes.data.data);

      // Fetch initial order breakdown
      await fetchOrderBreakdown();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderBreakdown = async () => {
    try {
      const response = await dashboardEnhancedAPI.getOrderBreakdown({ period: selectedPeriod });
      setOrderBreakdown(response.data.data);
    } catch (error) {
      console.error('Error fetching order breakdown:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Prepare chart data
  const orderTypeData = orderBreakdown && orderBreakdown.orderTypes ? 
    orderBreakdown.orderTypes.map((item, index) => ({
      name: item.type === 'normal' ? 'Normal Orders' : 'Subscription Orders',
      value: item.count,
      color: COLORS[index]
    })) : [];

  const orderStatusData = orderBreakdown && orderBreakdown.statuses ? 
    orderBreakdown.statuses.map((item, index) => ({
      name: item.status.charAt(0).toUpperCase() + item.status.slice(1).replace(/_/g, ' '),
      value: item.count,
      color: COLORS[index % COLORS.length]
    })) : [];

  const subscriptionStatusData = subscriptionAnalytics && subscriptionAnalytics.statusBreakdown ? 
    subscriptionAnalytics.statusBreakdown.map((item, index) => ({
      name: item.status.charAt(0).toUpperCase() + item.status.slice(1),
      value: item.count,
      color: COLORS[index % COLORS.length]
    })) : [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Refresh Data
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Today's Orders"
            value={stats.todayOrders || 0}
            icon={<ShoppingCart className="w-8 h-8" />}
            color="blue"
            subtitle={`${stats.todayNormalOrders || 0} normal, ${stats.todaySubscriptionOrders || 0} subscription`}
          />
          <StatCard
            title="Today's Revenue"
            value={`₹${(stats.todayRevenue || 0).toLocaleString()}`}
            icon={<DollarSign className="w-8 h-8" />}
            color="green"
          />
          <StatCard
            title="Active Subscriptions"
            value={stats.activeSubscriptions || 0}
            icon={<Package className="w-8 h-8" />}
            color="purple"
          />
          <StatCard
            title="Pending Deliveries"
            value={stats.pendingDeliveries || 0}
            icon={<Truck className="w-8 h-8" />}
            color="orange"
          />
        </div>
      )}

      {/* Quick Action Buttons */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg shadow-md p-6 border border-blue-200 dark:border-blue-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <QuickActionButton
            icon={<Clock className="w-5 h-5" />}
            label="Daily Orders"
            description="Today's orders"
            onClick={() => navigate('/todays-orders')}
            color="blue"
          />
          <QuickActionButton
            icon={<ShoppingCart className="w-5 h-5" />}
            label="All Orders"
            description="Complete history"
            onClick={() => navigate('/orders')}
            color="emerald"
          />
          <QuickActionButton
            icon={<Package className="w-5 h-5" />}
            label="Subscriptions"
            description="Manage plans"
            onClick={() => navigate('/subscriptions')}
            color="purple"
          />
          <QuickActionButton
            icon={<Zap className="w-5 h-5" />}
            label="Scheduler"
            description="Automation status"
            onClick={() => navigate('/scheduler')}
            color="amber"
          />
          <QuickActionButton
            icon={<Truck className="w-5 h-5" />}
            label="Deliveries"
            description="Manage groups"
            onClick={() => navigate('/delivery-management-new')}
            color="orange"
          />
        </div>
      </div>

      {/* Revenue Trends Chart */}
      {revenueTrends.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Revenue Trends (Last 30 Days)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis />
              <Tooltip 
                formatter={(value) => `₹${(value || 0).toLocaleString()}`}
                labelFormatter={(date) => new Date(date).toLocaleDateString()}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="totalRevenue" 
                stroke={COLORS[0]} 
                strokeWidth={2}
                name="Total Revenue"
              />
              <Line 
                type="monotone" 
                dataKey="normalRevenue" 
                stroke={COLORS[1]} 
                strokeWidth={2}
                name="Normal Orders"
              />
              <Line 
                type="monotone" 
                dataKey="subscriptionRevenue" 
                stroke={COLORS[2]} 
                strokeWidth={2}
                name="Subscription Orders"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Order Breakdown */}
      {orderBreakdown && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Order Analysis</h2>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="all">All Time</option>
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Order Type Breakdown */}
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-4 text-center">Orders by Type</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={orderTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {orderTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Order Status Breakdown */}
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-4 text-center">Orders by Status</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={orderStatusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill={COLORS[0]}>
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-4 text-center text-gray-600">
            <p className="text-lg font-semibold">Total Orders: {orderBreakdown.totalOrders}</p>
          </div>
        </div>
      )}

      {/* Quick View - Today's and Tomorrow's Orders */}
      {quickView && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Orders */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Today's Orders (Top 5)
            </h2>
            {quickView.todayOrders.length > 0 ? (
              <div className="space-y-3">
                {quickView.todayOrders.map((order) => (
                  <OrderCard key={order.uuid} order={order} />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No orders for today</p>
            )}
          </div>

          {/* Tomorrow's Orders */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Tomorrow's Orders (Top 5)
            </h2>
            {quickView.tomorrowOrders.length > 0 ? (
              <div className="space-y-3">
                {quickView.tomorrowOrders.map((order) => (
                  <OrderCard key={order.uuid} order={order} />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No orders for tomorrow</p>
            )}
          </div>
        </div>
      )}

      {/* Weekly Summary */}
      {quickView && (
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md p-6 text-white">
          <h2 className="text-xl font-semibold mb-4">This Week's Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-blue-100 text-sm">Total Orders</p>
              <p className="text-3xl font-bold">{quickView.weeklySummary.totalOrders || 0}</p>
            </div>
            <div>
              <p className="text-blue-100 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold">₹{(quickView.weeklySummary.totalRevenue || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-blue-100 text-sm">Order Split</p>
              <p className="text-lg font-semibold">
                {quickView.weeklySummary.normalOrders || 0} Normal / {quickView.weeklySummary.subscriptionOrders || 0} Subscription
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Analytics */}
      {subscriptionAnalytics && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Analytics</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Subscription Status */}
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-4 text-center">Subscription Status</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={subscriptionStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {subscriptionStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Company Breakdown */}
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-4">Top Companies</h3>
              <div className="space-y-3">
                {subscriptionAnalytics.companyBreakdown.slice(0, 5).map((company, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{company.companyName || 'N/A'}</p>
                      <p className="text-sm text-gray-600">{company.activeSubscriptions || 0} active subscriptions</p>
                    </div>
                    <p className="text-lg font-semibold text-green-600">
                      ₹{(company.totalRevenue || 0).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Upcoming Deliveries */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-600 text-sm font-medium">Today's Deliveries</p>
              <p className="text-2xl font-bold text-blue-900">{subscriptionAnalytics.upcomingDeliveries.today}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-green-600 text-sm font-medium">Tomorrow's Deliveries</p>
              <p className="text-2xl font-bold text-green-900">{subscriptionAnalytics.upcomingDeliveries.tomorrow}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-purple-600 text-sm font-medium">This Week's Deliveries</p>
              <p className="text-2xl font-bold text-purple-900">{subscriptionAnalytics.upcomingDeliveries.thisWeek}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className={`${colorClasses[color]} text-white p-3 rounded-lg`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Order Card Component
const OrderCard = ({ order }) => {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-purple-100 text-purple-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{order.orderNumber || 'N/A'}</p>
          <p className="text-sm text-gray-600">{order.customerName || 'N/A'}</p>
          <p className="text-xs text-gray-500 mt-1">
            {order.orderType === 'subscription' ? '📦 Subscription' : '🛒 Normal'} • {order.deliveryTimeSlot || 'N/A'}
          </p>
        </div>
        <div className="text-right">
          <p className="font-bold text-gray-900">₹{(order.totalAmount || 0).toLocaleString()}</p>
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
            {order.status ? order.status.replace('_', ' ') : 'Unknown'}
          </span>
        </div>
      </div>
    </div>
  );
};

// Quick Action Button Component
const QuickActionButton = ({ icon, label, description, onClick, color }) => {
  const colorClasses = {
    blue: 'hover:bg-blue-100 dark:hover:bg-blue-900/40 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400',
    emerald: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border-emerald-300 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400',
    purple: 'hover:bg-purple-100 dark:hover:bg-purple-900/40 border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400',
    amber: 'hover:bg-amber-100 dark:hover:bg-amber-900/40 border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400',
    orange: 'hover:bg-orange-100 dark:hover:bg-orange-900/40 border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400'
  };

  return (
    <button
      onClick={onClick}
      className={`p-4 border-2 rounded-lg transition-all duration-200 text-left hover:shadow-md ${colorClasses[color]}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-2xl">{icon}</div>
        <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <p className="font-semibold text-sm">{label}</p>
      <p className="text-xs opacity-75">{description}</p>
    </button>
  );
};

export default DashboardEnhanced;